import React, { useCallback } from 'react';
import { Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { HOTKEY_TYPE, useFramework } from '@/framework';
import { useLocaleStore } from '@/i18n';
import {
    BaseLayerViewType,
    LayerType,
    PluginEnum,
    type IGroupLayerProps,
    type IImageLayerProps,
} from '@/version-types';
import { ContextMenuPlugin } from '../../context-menu';
import { ContextMenuPluginDependency } from '../../dependency';
import { useContextMenuState } from '../../states/context-menu-state';
import { ContextMenuEvents, SHORTCUT_FUNC_MAP } from '../../type';
import ImageLayerAiItem from './image-layer-ai-item/image-layer-ai-item';

interface IShortcutItem {
    name: string;
    title: string;
    onClick: (e: React.MouseEvent) => void;
    onMouseDown?: (e: React.MouseEvent) => void;
}

const ShortcutItem: React.FC<IShortcutItem> = React.memo(({ name, title, onClick, onMouseDown }) => {
    return (
        <Tooltip placement="top" title={title}>
            <div
                className="shortcut-list__item"
                onMouseDown={onMouseDown}
                data-type={BaseLayerViewType.SHORTCUT}
                onClick={onClick}
            >
                <div className="link-icon">
                    <IconFont fontSize={16} name={name} />
                </div>
            </div>
        </Tooltip>
    );
});

/**
 * AI花字
 */
const AiItem: React.FC<{ onClick?: (e: React.MouseEvent) => void }> = React.memo(({ onClick }) => {
    return (
        <Tooltip placement="top" title="AI花字">
            <div className="shortcut-list__item " data-type={BaseLayerViewType.SHORTCUT} onClick={(e) => onClick?.(e)}>
                <div className="l-operate__none-event link-icon mt-ai-nav">
                    <IconFont name="AiTools" fontSize={16} color="var(--content-list-primary)" />
                </div>
            </div>
        </Tooltip>
    );
});

/**
 * 图表-编辑数据
 */
const ChartItem: React.FC<{ onClick?: (e: React.MouseEvent) => void }> = React.memo(({ onClick }) => {
    const { $t } = useLocaleStore();
    return (
        <div
            className="shortcut-list__item mt-chart-editor-data-nav"
            data-type={BaseLayerViewType.SHORTCUT}
            onClick={(e) => onClick?.(e)}
        >
            <div className="link-icon">
                <IconFont name="EditBold" fontSize={16} color="var(--content-system-primary)" />
                <span>{$t((v) => v.baseLayer.shortcutOperate.editChartData)}</span>
            </div>
        </div>
    );
});

const DefaultShortcutList: React.FC<{ id: string }> = ({ id }) => {
    const framework = useFramework();
    const showAdvancedAIOperation = useContextMenuState()((state) => state.showAdvancedAIOperation);
    const layer = framework.getLayer(id);
    const activePickLayer = framework.operate.getActivePickLayer();
    const menuList = framework.operate.shortcutMenu.getCustomMenu();
    const replaceMenu = framework.operate.shortcutMenu.getReplaceMenu();

    const { $t } = useLocaleStore();

    const onAi = useCallback(
        (e) => {
            e.stopPropagation();
            framework.events.emit(ContextMenuEvents.openPlugin, { func: PluginEnum.AI_TEXT });
            framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.AI_TEXT);
        },
        [id],
    );

    const onChart = useCallback(
        (e) => {
            e.stopPropagation();

            framework.getInstance(ContextMenuPluginDependency).openChartEditor(id);
        },
        [id],
    );

    // 进入文本编辑态
    const onEditText = useCallback(
        (e) => {
            e.stopPropagation();
            if (!activePickLayer?.id) return;

            // 进入编辑态
            framework.getInstance(ContextMenuPluginDependency).enterEditing(activePickLayer.id);
            framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.editText);
        },
        [activePickLayer?.id],
    );

    if (!layer) return null;

    const isGroup = layer?.layerType === LayerType.GROUP_LAYER;
    const isImageLayer = layer?.layerType === LayerType.IMAGE_LAYER;
    const isExtraGroup = (layer.data as IGroupLayerProps)?.isExtraGroup;
    const showBrokeGroup = framework.checkBrokenGroup(layer);
    const isGroupExtension = framework.checkIsGroupExtension(layer);
    const isWatermark = layer?.layerType === LayerType.WATERMARK;

    // 是否拥有ai花字功能
    const hasAiText =
        (layer?.layerType === LayerType.IMAGE_LAYER && (layer.data as IImageLayerProps).aiTextExtension) ||
        layer?.layerType === LayerType.TEXT_LAYER;

    // 完全自定义菜单
    if (replaceMenu) {
        return (
            <div className="shortcut-menu" onMouseDown={(e) => e.stopPropagation()}>
                {replaceMenu}
            </div>
        );
    }

    // 默认菜单
    return (
        <div
            className="shortcut-menu"
            onMouseDown={(e) => {
                e.stopPropagation();
                framework.getInstance(ContextMenuPlugin).close();
            }}
        >
            {menuList
                .filter((item) => item.layerType === activePickLayer?.layerType)
                .map((item) => {
                    return <React.Fragment key={item.menu}>{item.data}</React.Fragment>;
                })}
            {showAdvancedAIOperation && hasAiText && !isImageLayer ? <AiItem onClick={onAi} /> : null}
            {layer.layerType === LayerType.CHART && <ChartItem onClick={onChart} />}
            {isGroup && !isExtraGroup ? (
                showBrokeGroup ? (
                    <ShortcutItem
                        name="DelinkGroupBold"
                        title={$t((v) => v.baseLayer.shortcutOperate.unGroup)}
                        onClick={(e) => {
                            e.stopPropagation();
                            framework.events.emit(HOTKEY_TYPE.brokeGroup);
                            framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.brokeGroup);
                        }}
                    />
                ) : isGroupExtension ? null : (
                    <ShortcutItem
                        name="DelinkGroupBold"
                        title={$t((v) => v.baseLayer.shortcutOperate.unSupportGroup)}
                        onClick={() => {}}
                    />
                )
            ) : null}
            {isExtraGroup ? (
                <ShortcutItem
                    name="GroupLinkBold"
                    title={$t((v) => v.baseLayer.shortcutOperate.group)}
                    onClick={(e) => {
                        e.stopPropagation();
                        framework.events.emit(HOTKEY_TYPE.makeGroup);
                        framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.makeGroup);
                    }}
                />
            ) : null}

            {showAdvancedAIOperation && isImageLayer ? <ImageLayerAiItem hasAiText={!!hasAiText} /> : null}

            {activePickLayer?.layerType === LayerType.VECTOR_LAYER ? (
                <ShortcutItem
                    name="TextSquareBold"
                    title={$t((v) => v.baseLayer.shortcutOperate.editText)}
                    onClick={onEditText}
                />
            ) : null}

            {!isWatermark ? (
                <ShortcutItem
                    name="DuplicateBold"
                    title={$t((v) => v.baseLayer.shortcutOperate.copy)}
                    onClick={(e) => {
                        e.stopPropagation();
                        framework.events.emit(HOTKEY_TYPE.createCopy);
                        framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.createCopy);
                    }}
                />
            ) : null}
            {activePickLayer?.layerType === LayerType.TEXT_LAYER ? (
                <ShortcutItem
                    name="FormatPainterBold"
                    title={$t((v) => v.baseLayer.shortcutOperate.copyStyle)}
                    onClick={(e) => {
                        e.stopPropagation();
                        framework.getInstance(ContextMenuPluginDependency).extractFormatBrushData(
                            activePickLayer.id,
                            $t((v) => v.baseLayer.shortcutOperate.cursorTip),
                        );
                        framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.textBrush);
                    }}
                />
            ) : null}
            {activePickLayer?.layerType === LayerType.IMAGE_LAYER ? (
                <ShortcutItem
                    name="FormatPainterBold"
                    title={$t((v) => v.baseLayer.shortcutOperate.copyEffect)}
                    onClick={(e) => {
                        e.stopPropagation();
                        framework.getInstance(ContextMenuPluginDependency).copyImageEffectData(
                            activePickLayer.id,
                            $t((v) => v.baseLayer.shortcutOperate.cursorTip),
                        );
                        framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.imageBrush);
                    }}
                />
            ) : null}
            <ShortcutItem
                name="TrashCanBold"
                title={$t((v) => v.baseLayer.shortcutOperate.delete)}
                onClick={(e) => {
                    e.stopPropagation();
                    framework.events.emitDelete('shortcut');
                    framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.delete);
                }}
            />
            <ShortcutItem
                name="EllipsisBold"
                title={$t((v) => v.baseLayer.shortcutOperate.more)}
                onMouseDown={(e) => {
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    // setShowImageAiMenu(false);
                    const { top, height, left } = e.currentTarget.getBoundingClientRect();
                    framework.getInstance(ContextMenuPlugin).show({
                        x: left,
                        y: top + height + 10,
                    });
                    framework.events.emitShortcutEvent(SHORTCUT_FUNC_MAP.more);
                }}
            />
        </div>
    );
};

export default DefaultShortcutList;
